<template>
  <div class="space-y-3">
    <!-- Main comment -->
    <div v-if="!commentState.edit_comment" class="flex gap-3 items-start">
      <div class="flex-1 min-w-0">
        <div class="bg-surface-2 rounded-lg p-3 md:p-4 shadow-sm">
          <!-- Header row: avatar, name, date -->
          <div class="flex items-center gap-2 mb-2">
            <div
              class="w-8 h-8 md:w-10 md:h-10 shrink-0 bg-primary text-white rounded-full flex items-center justify-center text-xs md:text-sm font-medium"
            >
              {{ getUserInitials(comment.user) }}
            </div>
            <div class="flex flex-col">
              <span class="font-medium text-sm text-ink">

                {{ getUserName(comment) }}
              </span>
              <span class="text-xs text-ink-mute">
                {{ formatTimestampWithBrowserTimezone(comment.created_at, comment.timezone) }}
              </span>
              <span
                ><small class="text-xs text-ink-mute">{{ evaluateRole(comment) }}</small></span
              >
            </div>
          </div>

          <!-- Parent message quote (for replies) -->
          <div
            v-if="comment.parent_message"
            class="bg-surface border-l-4 p-2 mb-2 text-[14px] italic"
            style="border-color: var(--border); color: var(--ink-soft)"
            v-html="sanitize(comment.parent_message)"
          >
          </div>

          <!-- Machine-translation marker (AB#21162). Shown only while the
               translation is on screen, so an agent is never in doubt about
               whether they are reading the sender's words or a machine's.

               "Machine translation" leads and "by ELSA" follows, deliberately.
               The first half is a safety label — it is what stops an agent
               quoting generated text back to a customer as their own words —
               and it has to be the part the eye lands on. The attribution is
               worth having, but not at the cost of burying the warning. -->
          <div v-if="showingTranslation" class="flex items-center gap-2 mb-1.5" data-test="comment-translated-badge">
            <span
              class="inline-flex items-center gap-1 rounded-full bg-primary-light px-2 py-0.5 text-[11px] font-medium text-primary-dark"
            >
              <LanguageIcon class="w-3 h-3" />
              Machine translation by ELSA
            </span>
            <span v-if="sourceLanguage" class="text-xs text-ink-mute">
              from {{ languageName(sourceLanguage) }}
            </span>
          </div>

          <!-- Comment message. The translation is rendered as text, never as HTML:
               it did not come from our database and must not be able to introduce
               markup into the page. The original keeps its formatting. -->
          <div
            v-if="showingTranslation"
            class="mb-2 whitespace-pre-line"
            style="color: var(--ink-soft); font-size: 14px"
            data-test="comment-translation"
          >{{ translated }}</div>
          <div
            v-else
            class="mb-2"
            style="color: var(--ink-soft); font-size: 14px"
            v-html="sanitize(linkifyText(comment.message))"
          >
          </div>

          <!-- A failed translation says so beside the comment and changes nothing
               else: the original is still exactly where it was. -->
          <div
            v-if="translationError"
            class="flex items-center gap-1.5 mb-2 text-xs text-red-700"
            data-test="comment-translation-error"
          >
            <ExclamationCircleIcon class="w-3.5 h-3.5 shrink-0" />
            {{ translationError }}
          </div>

          <!-- Action buttons always visible below comment -->
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
            <!-- Resolve action -->
            <label
              v-if="canResolveComment(comment)"
              class="flex items-center gap-1 cursor-pointer"
              style="color: var(--ink-soft)"
            >
              <input
                type="checkbox"
                :checked="!!comment.action_taken"
                @change="handleResolveToggle"
                class="w-3 h-3"
                :disabled="isResolving"
              />
              <span>
                {{
                  comment.action_taken
                    ? comment.action_taken_by
                      ? `Resolved by ${comment.action_taken_by}`
                      : 'Resolved'
                    : 'Take Action'
                }}
              </span>
            </label>

            <!-- Edit button -->
            <button
              v-if="canEditComment(comment)"
              @click="toggleEdit"
              class="px-2 py-1 rounded"
              style="color: var(--ink-soft)"
            >
              <PencilIcon class="w-4 h-4 inline mr-1" />
              Edit
            </button>

            <!-- Delete button -->
            <button
              v-if="canDeleteComment(comment)"
              @click="handleDelete"
              class="px-2 py-1 rounded"
              style="color: var(--ink-soft)"
            >
              <TrashIcon class="w-4 h-4 inline mr-1" />
              Delete
            </button>
            <!-- Reply button -->
            <button @click="toggleReply" class="px-2 py-1 rounded" style="color: var(--ink-soft)">
              <ArrowUturnLeftIcon class="w-4 h-4 inline mr-1" />
              Reply
            </button>

            <!-- Translate (AB#21162). Offered from the server's `translatable`
                 flag, never from a second copy of the language check here.

                 Labelled "Ask ELSA for Translation" rather than "Translate"
                 because the result is a machine translation and the label is the
                 only warning an agent gets before they read it as the sender's
                 own words. Naming who did it sets that expectation up front; the
                 badge on the result repeats it afterwards.

                 ELSA, not Atlas: Atlas is DDI's agent and ELSA is Operations',
                 and an order timeline is an Operations surface. The earlier
                 Atlas label came from the repository's commit-attribution
                 convention, which has no business on a user-facing control. -->
            <button
              v-if="comment.translatable && !showingTranslation"
              :disabled="isTranslating"
              class="px-2 py-1 rounded disabled:cursor-default"
              :style="{ color: isTranslating ? 'var(--ink-mute)' : 'var(--ink-soft)' }"
              data-test="comment-translate"
              @click="translate"
            >
              <ArrowPathIcon v-if="isTranslating" class="w-4 h-4 inline mr-1 animate-spin" />
              <LanguageIcon v-else class="w-4 h-4 inline mr-1" />
              {{ isTranslating ? 'Translating…' : 'Ask ELSA for Translation' }}
            </button>

            <button
              v-if="showingTranslation"
              class="px-2 py-1 rounded"
              style="color: var(--ink-soft)"
              data-test="comment-show-original"
              @click="showOriginal"
            >
              <ArrowUturnLeftIcon class="w-4 h-4 inline mr-1" />
              Show original
            </button>
          </div>

          <!-- Comment files -->
          <div v-if="comment.files?.length" class="flex flex-wrap gap-2 mb-2">
            <div
              v-for="(file, index) in comment.files"
              :key="`comment-file-${index}`"
              role="button"
              tabindex="0"
              :aria-label="`Open file ${file.name}`"
              class="relative group cursor-pointer"
              @click="handleFileClick(file)"
              @keydown="triggerClickOnEnterOrSpace"
            >
              <div class="w-16 h-12 bg-surface-2 border rounded overflow-hidden">
                <img
                  v-if="isImageFile(file.extension)"
                  :src="`${storageUrl}/${file.url}`"
                  :alt="file.name"
                  class="w-full h-full object-cover pointer-events-none"
                />
                <div v-else class="w-full h-full flex items-center justify-center">
                  <DocumentIcon class="w-6 h-6 text-ink-mute" />
                </div>
              </div>
              <div
                class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate"
              >
                {{ file.name }}
              </div>
            </div>
          </div>

          <!-- Comment metadata -->
          <div class="flex items-center justify-between text-xs text-ink-mute">
            <div class="flex items-center gap-2">
              <span v-if="comment.action_taken && comment.action_taken_by" class="text-green-600">
                • Resolved by {{ comment.action_taken_by }} at
                {{ formatDate(comment.action_taken_at) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit form -->
    <div v-if="commentState.edit_comment" class="ml-0 md:ml-11">
      <CommentForm
        mode="edit"
        :edit-comment="comment"
        :loading="loading"
        :storage-url="storageUrl"
        @submit="handleEditSubmit"
        @cancel="cancelEdit"
      />
    </div>

    <!-- Reply form -->
    <div v-if="commentState.reply_comment" class="ml-0 md:ml-11">
      <CommentForm
        mode="reply"
        :parent-comment="comment"
        :loading="loading"
        :storage-url="storageUrl"
        @submit="handleReplySubmit"
        @cancel="cancelReply"
      />
    </div>

    <!-- Child comments (replies) -->
    <div v-if="comment.children?.length" class="mt-4">
      <div class="ml-1 pl-2 md:ml-8 md:pl-4 space-y-3" style="border-left: 2px solid var(--border)">
        <CommentItem
          v-for="child in comment.children"
          :key="`child-comment-${child.id}-${refreshKey}`"
          :comment="child"
          :storage-url="storageUrl"
          :loading="loading"
          @edit-comment="(comment, data, editCommentData) => $emit('editComment', comment, data, editCommentData)"
          @delete-comment="(comment) => $emit('deleteComment', comment)"
          @reply-comment="(parentComment, data) => $emit('replyComment', parentComment, data)"
          @resolve-comment="(comment, actionTaken) => $emit('resolveComment', comment, actionTaken)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from '@/composables';
import { showConfirm } from '@/composables/swal';
import { sanitize } from '@/utils/sanitize';
import { usePermissions } from '@/composables/usePermissions';
import { formatTimestampWithBrowserTimezone } from '@/composables/useTimeZoneFormatDate';
import { Comment } from '@/models';
import { useMainStore } from '@/store'; // Import your main store
import type { CommentFile, CommentFormData } from '@/types/comments'; // Removed User from here
import { triggerClickOnEnterOrSpace } from '@/utils/a11y';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  DocumentIcon,
  ExclamationCircleIcon,
  LanguageIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline';
import { languageName, useTranslation } from '@/composables/useTranslation';
import { storeToRefs } from 'pinia'; // Import storeToRefs
import { inject, reactive, ref, watch } from 'vue';
import CommentForm from './CommentForm.vue';

interface Props {
  comment: Comment;
  storageUrl?: string;
  loading?: boolean;
  isResolving?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  storageUrl: import.meta.env.VITE_FILE_URL || '',
  loading: false,
  isResolving: false,
});

const emit = defineEmits<{
  editComment: [comment: Comment, data: CommentFormData, editCommentData: Comment];
  deleteComment: [comment: Comment];
  replyComment: [parentComment: Comment, data: CommentFormData];
  resolveComment: [comment: Comment, actionTaken: boolean];
}>();

// Inject refresh key from parent to force re-renders
const refreshKey = inject('refreshKey', ref(0));

const commentState = reactive({
  edit_comment: false,
  reply_comment: false,
  show_actions: false,
});

const mainStore = useMainStore();
const { user: currentUser } = storeToRefs(mainStore);

const { canEditComment, canDeleteComment, canResolveComment } = usePermissions();

// AB#21162. Scoped to this comment: the original stays untouched in
// `comment.message` and the toggle is local, so switching back costs nothing and
// the sender's own words are never overwritten.
const {
  translated,
  sourceLanguage,
  showingTranslation,
  isTranslating,
  error: translationError,
  translate,
  showOriginal,
} = useTranslation('order_comment', props.comment.id);

const getUserInitials = (user: any): string => {
  if (!user) return '';
  let name = user.name;
  if (!name && user.first_name) {
    name = user.first_name;
  }
  if (!name) return '';
  const nameArray = name.split(' ');
  const firstLetter = name.charAt(0);
  let lastLetter = '';
  if (nameArray.length > 1) {
    lastLetter = nameArray[nameArray.length - 1].charAt(0);
  }
  return (firstLetter + lastLetter).toUpperCase();
};

const handleEditSubmit = (data: CommentFormData, editCommentData?: Comment) => {
  emit('editComment', props.comment, data, editCommentData);
  commentState.edit_comment = false;
};

const handleReplySubmit = (data: CommentFormData) => {
  if (!data) return;

  const replyData: CommentFormData = {
    ...data,
    parent_message_id: props.comment.id,
    parent_message: props.comment.message,
  };

  emit('replyComment', props.comment, replyData);
  commentState.reply_comment = false;
};

const handleDelete = async () => {
  const confirmed = await showConfirm(
    'Delete Comment',
    'Are you sure you want to delete this comment?'
  );
  if (confirmed) {
    emit('deleteComment', props.comment);
  }
};

const handleResolveToggle = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('resolveComment', props.comment, target.checked);
};

const handleFileClick = (file: CommentFile) => {
  const url = `${props.storageUrl}/${file.url}`;
  window.open(url, '_blank');
};

const toggleReply = () => {
  commentState.reply_comment = !commentState.reply_comment;
  commentState.edit_comment = false;
};

const toggleEdit = () => {
  commentState.edit_comment = !commentState.edit_comment;
  commentState.reply_comment = false;
};

const cancelReply = () => {
  commentState.reply_comment = false;
};

const cancelEdit = () => {
  commentState.edit_comment = false;
};

watch(
  () => props.comment,
  (newComment) => {
    commentState.edit_comment = newComment.edit_comment || false;
    commentState.reply_comment = newComment.reply_comment || false;
    commentState.show_actions = newComment.show_actions || false;
  },
  { immediate: true }
);

const isImageFile = (extension: string): boolean => {
  if (!extension) return false;
  return ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'webp'].includes(extension.toLowerCase());
};

// Evaluate the user's role for display under the comment
const evaluateRole = (activity_comment: any): string => {
  if (Object.prototype.hasOwnProperty.call(activity_comment, 'user') && activity_comment.user) {
    if (Object.prototype.hasOwnProperty.call(activity_comment.user, 'userroles')) {
      if (activity_comment.user.userroles.length > 0) {
        switch (activity_comment.user.userroles[0].name.toLowerCase()) {
          case 'factory':
            return 'Factory';
          case 'admin':
            return 'Merchant';
          case 'superadmin':
            return 'Custimoo Admin';
          case 'orderadministrator':
            return 'Order administrator';
        }
      }
    } else {
      return 'Customer';
    }
  } else {
    return 'Customer';
  }
  return '';
};

const getUserName = (comment: any): string => {
  if(comment?.name){
    return comment.name;
  }
  if (comment?.user) {
    if(evaluateRole(comment) === 'Customer') {
      return comment.user?.first_name + ' ' + comment.user?.last_name;
    }
    else{
      return comment.user?.name || 'User';
    }
  } else {
    return 'User';
  }
};

function linkifyText(text) {
   if (!text) return '';
  const urlRegex = /((https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?)/gi;

  return text.replace(urlRegex, (match) => {
    let url = match;

    // If URL has no protocol, add https://
    if (!/^https?:\/\//i.test(url)) {
      url = "https://" + url;
    }

    return `<a href="${url}" class="text-blue-500 hover:underline " style="font-size:12px !important" target="_blank" rel="noopener noreferrer">${match}</a>`;
  });
}
</script>
